import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  FinishedImport,
  Schedule,
  CatalogResponse,
} from "@vviinn/user-managment-client-ts";
import { FetchableState } from "..";
import { ApiRequest, serializeModel } from "../../../api";
import {
  GetImportHistoryRequest,
  ScheduleFeedImportRequest,
  TriggerFeedImportRequest,
} from "./types";
import { fetchSuccinctSites } from "../sites/sites-slice";
import { StringifiedImport } from "../../../feed/types";

export type ProductFeedState = FetchableState & {
  list: StringifiedImport[];
  feedIsScheduling: boolean;
  feedIsTriggering: boolean;
};

const initialState: ProductFeedState = {
  list: [],
  loading: false,
  error: false,
  status: "idle",
  feedIsScheduling: false,
  feedIsTriggering: false,
};

export const fetchSiteFeed = createAsyncThunk(
  "feed/fetchSiteFeed",
  async ({ api, value }: ApiRequest<GetImportHistoryRequest>) => {
    const { companyId, siteId } = value;
    const response = await api.getSiteImportHistory(companyId, siteId);
    const stringModel = serializeModel<Array<FinishedImport>>(response.value);
    return (stringModel as unknown as StringifiedImport[]) //serializeModel returns occured at as string but still assumes that it is Date, therefore typecast
      .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
  },
);

export const scheduleSiteFeed = createAsyncThunk(
  "feed/scheduleSiteFeed",
  async ({ api, value }: ApiRequest<ScheduleFeedImportRequest>) => {
    const { companyId, siteId, schedule } = value;
    const response = await api.upsertSiteImportSchedule(
      companyId,
      siteId,
      schedule,
    );
    return serializeModel<Schedule>(response.value);
  },
);

export const triggerSiteFeed = createAsyncThunk(
  "feed/triggerSiteFeed",
  async ({ api, value }: ApiRequest<TriggerFeedImportRequest>) => {
    const { companyId, siteId } = value;
    const response = await api.triggerSiteImportSchedule(companyId, siteId);
    return serializeModel<CatalogResponse>(response.value);
  },
);

const feedSlice = createSlice({
  name: "feed",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchSiteFeed.pending, (state) => {
      state.error = false;
      state.loading = true;
      state.status = "loading";
    });
    builder.addCase(fetchSiteFeed.rejected, (state) => {
      state.error = true;
      state.loading = false;
      state.status = "error";
      state.list = initialState.list;
    });
    builder.addCase(fetchSiteFeed.fulfilled, (state, { payload }) => {
      state.error = false;
      state.loading = false;
      state.list = payload;
      state.status = "fulfilled";
    });

    builder.addCase(scheduleSiteFeed.pending, (state) => {
      state.feedIsScheduling = true;
    });
    builder.addCase(scheduleSiteFeed.fulfilled, (state) => {
      state.feedIsScheduling = false;
    });
    builder.addCase(scheduleSiteFeed.rejected, (state) => {
      state.feedIsScheduling = false;
    });

    builder.addCase(triggerSiteFeed.pending, (state) => {
      state.feedIsTriggering = true;
    });
    builder.addCase(triggerSiteFeed.fulfilled, (state) => {
      state.feedIsTriggering = false;
    });
    builder.addCase(triggerSiteFeed.rejected, (state) => {
      state.feedIsTriggering = false;
    });

    builder.addCase(fetchSuccinctSites.fulfilled, (state, payload) => {
      if (payload.payload.length === 0) {
        state.list = initialState.list;
      }
    });
    builder.addCase(fetchSuccinctSites.rejected, (state) => {
      state.list = initialState.list;
    });
  },
});

export default feedSlice.reducer;
